import React, { useEffect, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { BereichLink } from '../../pages/einstellungen/EinstLink';
import { stoerungsLeiste, type LeistenStoerung } from './hilfeApi';
import { hilfeAdresse } from './hilfeLeiste';
import { STOERUNG_TEXTE, stoerungLeisteSatz } from './hilfeTexte';
import './hilfe.css';

// Hinweisleiste in der App (Profil-Nachbau F-06, TEIL-3 SP3-05 „Hinweisleiste“; PF-311-1): oben über dem Inhalt, solange eine
// veröffentlichte Störung ab „Teilweise eingeschränkt“ läuft. Geladen beim Einhängen, danach alle 10 Minuten, solange das
// Fenster sichtbar ist, und sofort beim Wechsel auf sichtbar. Bei jedem Fehler (etwa 401 oder in einer Sicht) zeigt sie nichts.
// „Ausblenden“ gilt für diese Störung in dieser Browsersitzung.

export const AUSGEBLENDET_SCHLUESSEL = 'hilfe-stoerung-ausgeblendet';
const TAKT_MS = 10 * 60 * 1000;
const AB_TEILWEISE = ['TEILWEISE', 'ERHEBLICH', 'AUSFALL'];

type Speicher = Pick<Storage, 'getItem' | 'setItem'> | null | undefined;
/** Der Speicher dieser Browsersitzung; schon der Zugriff kann werfen (gesperrte Website-Daten). */
const sitzung = (): Speicher => window.sessionStorage;

/** Die ausgeblendeten Störungen; ohne lesbaren Speicher keine. */
export function ausgeblendeteIds(speicher: () => Speicher): string[] {
  try {
    const roh = speicher()?.getItem(AUSGEBLENDET_SCHLUESSEL);
    const liste: unknown = roh ? JSON.parse(roh) : [];
    return Array.isArray(liste) ? liste.filter((id): id is string => typeof id === 'string') : [];
  } catch {
    return [];
  }
}

/** Merkt eine Störung als ausgeblendet; ohne Speicher geschieht nichts (die Leiste erscheint beim nächsten Laden wieder). */
export function ausblenden(speicher: () => Speicher, id: string): void {
  try {
    const ids = ausgeblendeteIds(speicher);
    if (!ids.includes(id)) speicher()?.setItem(AUSGEBLENDET_SCHLUESSEL, JSON.stringify([...ids, id]));
  } catch {
    // Kein Speicher: nur für diese Ansicht ausgeblendet.
  }
}

/** Sichtbar nur bei einer Störung mit Status ungleich „Behoben“, Auswirkung ab „Teilweise eingeschränkt“, nicht ausgeblendet. */
export const leisteZeigen = (stoerung: LeistenStoerung | null, ausgeblendet: string[]) =>
  !!stoerung && stoerung.status !== 'BEHOBEN' && AB_TEILWEISE.includes(stoerung.auswirkung) && !ausgeblendet.includes(stoerung.id);

export default function StoerungsLeiste() {
  const [stoerung, setStoerung] = useState<LeistenStoerung | null>(null);
  const [weg, setWeg] = useState<string[]>(() => ausgeblendeteIds(sitzung));

  useEffect(() => {
    let abbruch: AbortController | null = null;
    const laden = () => {
      if (document.visibilityState !== 'visible') return;
      abbruch?.abort();
      const aktuell = new AbortController();
      abbruch = aktuell;
      stoerungsLeiste(aktuell.signal)
        .then(daten => { if (!aktuell.signal.aborted) setStoerung(daten.stoerung); })
        .catch(() => { if (!aktuell.signal.aborted) setStoerung(null); });
    };
    laden();
    const takt = window.setInterval(laden, TAKT_MS);
    document.addEventListener('visibilitychange', laden);
    return () => { abbruch?.abort(); window.clearInterval(takt); document.removeEventListener('visibilitychange', laden); };
  }, []);

  if (!stoerung || !leisteZeigen(stoerung, weg)) return null;
  return <div className="hilfe-stoerungsleiste" role="status">
    <AlertTriangle size={16} aria-hidden="true" />
    <span className="hilfe-stoerungsleiste-text">{stoerungLeisteSatz(stoerung.titel, stoerung.status)}</span>
    <span className="hilfe-stoerungsleiste-wege">
      <BereichLink className="hilfe-stoerungsleiste-link" to={hilfeAdresse('stoerungen', stoerung.id)}>{STOERUNG_TEXTE.details}</BereichLink>
      <button type="button" className="hilfe-stoerungsleiste-knopf" onClick={() => { ausblenden(sitzung, stoerung.id); setWeg(ids => [...ids, stoerung.id]); }}>
        {STOERUNG_TEXTE.ausblenden}
      </button>
    </span>
  </div>;
}
